/**
 * Hook for fetching and deriving production data
 */

import { useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Fetch production data and derive filter options
 * @returns {Object} Query result with data, filterOptions, error, and isFetching
 */
export const useProductionData = () => {
  const { getAccessTokenSilently } = useAuth0();

  const {
    data: rawData,
    error,
    isFetching,
  } = useQuery(
    ["ui-report-production-master"],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/ui-report-production-master`,
        { headers }
      );
      return data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  // Derive filter options from raw data
  const filterOptions = useMemo(() => {
    if (!rawData) return { years: [], aquifers: [], aggSystems: [], wells: [] };

    const yearsSet = new Set();
    const aquifersMap = new Map();
    const aggSystemsMap = new Map();
    const wellsMap = new Map();
    let hasNoPermitRecords = false;

    for (const row of rawData) {
      if (row.report_year) yearsSet.add(row.report_year);
      if (row.aquifer_ndx)
        aquifersMap.set(row.aquifer_ndx, {
          ndx: row.aquifer_ndx,
          name: row.aquifer_name,
        });
      if (row.agg_system_ndx) {
        aggSystemsMap.set(row.agg_system_ndx, {
          ndx: row.agg_system_ndx,
          name: row.agg_system_name,
        });
      } else {
        // Track that we have records without permits
        hasNoPermitRecords = true;
      }
      if (row.well_ndx)
        wellsMap.set(row.well_ndx, {
          ndx: row.well_ndx,
          number: row.cuwcd_well_number,
          name: row.well_name,
          agg_system_ndx: row.agg_system_ndx,
        });
    }

    // Helper to check if name indicates "not aggregated" (sort to bottom)
    const isNotAggregated = (name) => {
      const lower = (name || "").toLowerCase();
      return lower === "not aggregated" || lower === "no permit";
    };

    // Build agg systems list, sorted alphabetically with "Not Aggregated"/"No Permit" at bottom
    const aggSystems = Array.from(aggSystemsMap.values()).sort((a, b) => {
      const aNotAgg = isNotAggregated(a.name);
      const bNotAgg = isNotAggregated(b.name);
      if (aNotAgg && !bNotAgg) return 1;
      if (!aNotAgg && bNotAgg) return -1;
      return (a.name || "").localeCompare(b.name || "");
    });

    // Add "No Permit" option at the end if there are records without permits
    if (hasNoPermitRecords) {
      aggSystems.push({
        ndx: null,
        name: "No Permit",
      });
    }

    return {
      years: Array.from(yearsSet).sort((a, b) => b - a),
      aquifers: Array.from(aquifersMap.values()).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
      aggSystems,
      wells: Array.from(wellsMap.values()).sort((a, b) =>
        (a.number || "").localeCompare(b.number || "")
      ),
    };
  }, [rawData]);

  return {
    rawData,
    filterOptions,
    error,
    isFetching,
  };
};
